import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone, Star } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1650535716978-eb644a8cf898?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="noise relative flex min-h-screen items-end overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={HERO_IMG}
          alt="Junk removal crew hauling debris"
          className="h-[120%] w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/70 to-[#0B0C0E]/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C0E]/80 via-transparent to-transparent" />

      {/* hazard top edge */}
      <div className="hazard absolute top-0 left-0 right-0 h-1.5 opacity-80" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 sm:px-8"
      >
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-8 inline-flex items-center gap-3 border border-[#282C34] bg-[#14171C]/70 px-4 py-2 backdrop-blur-md"
          data-testid="hero-status-badge"
        >
          <span className="pulse-dot h-2 w-2 rounded-full bg-[#FF9F00]" />
          <span className="font-mono-ind text-[11px] uppercase tracking-[0.25em] text-zinc-300">
            Operational in Payne · Noble · Pawnee · Creek · Logan Counties
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display text-[17vw] leading-[0.88] tracking-tight sm:text-[13vw] lg:text-[9.5rem]"
        >
          <MaskedLine delay={0.5}>BIG OR SMALL,</MaskedLine>
          <MaskedLine delay={0.65}>
            WE HAUL <span className="text-stroke">IT</span>
          </MaskedLine>
          <MaskedLine delay={0.8}>
            <span className="text-[#FF5A00]">ALL.</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg"
        >
          Furniture, appliances, yard debris, full property clean-outs — GW Junk
          Solutions makes junk removal simple, stress-free, and affordable.
          Locally owned. On time. Clutter-free.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#quote"
            data-testid="hero-quote-cta"
            onClick={(e) => {
              e.preventDefault();
              window.__lenis
                ? window.__lenis.scrollTo("#quote", { offset: -72 })
                : document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="glow-orange group relative overflow-hidden bg-[#FF5A00] px-8 py-4 font-display text-xl tracking-widest text-[#0B0C0E] transition-transform duration-300 hover:-translate-y-0.5 active:translate-y-0"
          >
            <span className="relative z-10">GET A FREE QUOTE</span>
            <span className="absolute inset-0 -translate-x-full bg-[#FF9F00] transition-transform duration-500 ease-out group-hover:translate-x-0" />
          </a>
          <a
            href="tel:+19183997313"
            data-testid="hero-call-cta"
            className="group flex items-center gap-3 border border-zinc-600 px-8 py-4 font-display text-xl tracking-widest text-zinc-100 transition-all duration-300 hover:border-[#FF5A00] hover:text-[#FF9F00]"
          >
            <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
            918-399-7313
          </a>
          <div className="flex items-center gap-2 font-mono-ind text-xs uppercase tracking-widest text-zinc-400">
            <span className="flex text-[#FFC700]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-current" />
              ))}
            </span>
            Rated on Google
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 right-6 z-10 hidden items-center gap-2 font-mono-ind text-[10px] uppercase tracking-[0.3em] text-zinc-500 md:flex"
      >
        Scroll
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
}
